import { v4 as uuidv4 } from 'uuid';
import { VideoSegment } from '../../../types/Segment/VideoSegment';
import { SegmentType } from '../../../types/Segment/Type';
import { ElementType } from '../../../types/Element/Type';
import { Clip } from '@/domains/asset';
import { DEFAULT_COLOR } from '@/libs/core';
import { getClipFPS } from '@/libs/clipUtils';

export function updateVideoSegmentWithMetaData(clip: Clip, oldSegment: VideoSegment, offset: number): VideoSegment {
  const videoId = oldSegment.id;
  const segmentStartTime = oldSegment.segment_metadata?.start || 0;
  const segmentEndTime = oldSegment.segment_metadata?.end || 0;
  const segmentFPS = getClipFPS(clip);
  const duration = (segmentEndTime - segmentStartTime) * segmentFPS;
  const fromFrame = segmentStartTime * segmentFPS;
  const toFrame = segmentEndTime * segmentFPS;

  return {
    id: videoId,
    name: 'Video Segment',
    type: SegmentType.VIDEO,
    fromFrame: offset,
    toFrame: offset + duration,
    details: {
      background: {
        type: 'COLOR',
        value: DEFAULT_COLOR
      }
    },
    scenes: [],
    video: {
      id: videoId,
      name: 'Video Segment',
      type: ElementType.VIDEO,
      fromFrame,
      toFrame,
      details: {
        src: oldSegment.segment_metadata?.src || '',
        altText: 'Video Segment',
        volume: [
          {
            value: 100,
            fromFrame,
            toFrame
          }
        ]
      },
      trim: [],
      style: {
        objectFit: 'cover',
        width: '100%',
        height: '100%'
      },
      containerStyle: {
        borderRadius: '0px',
        position: 'absolute',
        top: '0%',
        left: '0%',
        width: '100%',
        height: '100%'
      },
      isOriginalVideo: false
    },
    maxBounds: {
      fromFrame: 0,
      toFrame: (oldSegment.segment_metadata?.maxVideoDuration || 0) * segmentFPS
    },
    currentBounds: {
      fromFrame: segmentStartTime * segmentFPS,
      toFrame: segmentEndTime * segmentFPS
    },
    segment_metadata: oldSegment.segment_metadata
  };
}

export function createVideoSegmentWithMetaData(
  clip: Clip,
  offset: number,
  duration: number,
  url: string,
  overridenVideoId?: string
): VideoSegment {
  const videoId = overridenVideoId || uuidv4();
  const segmentStartTime = 0;
  const segmentEndTime = duration || 0;
  const segmentFPS = getClipFPS(clip);
  const outroDuration = (segmentEndTime - segmentStartTime) * segmentFPS;
  const fromFrame = offset;
  const toFrame = offset + outroDuration;

  return {
    id: videoId,
    name: 'Video Segment',
    type: SegmentType.VIDEO,
    fromFrame,
    toFrame,
    details: {
      background: {
        type: 'COLOR',
        value: DEFAULT_COLOR
      }
    },
    scenes: [],
    video: {
      id: videoId,
      name: 'Video Segment',
      type: ElementType.VIDEO,
      fromFrame: 0,
      toFrame: outroDuration,
      details: {
        src: url,
        altText: 'Video Segment',
        volume: [
          {
            value: 100,
            fromFrame: 0,
            toFrame: outroDuration
          }
        ]
      },
      trim: [],
      style: {
        objectFit: 'cover',
        width: '100%',
        height: '100%'
      },
      containerStyle: {
        borderRadius: '0px',
        position: 'absolute',
        top: '0%',
        left: '0%',
        width: '100%',
        height: '100%'
      },
      isOriginalVideo: false
    },
    maxBounds: {
      fromFrame: offset * segmentFPS,
      toFrame: (offset + outroDuration) * segmentFPS
    },
    currentBounds: {
      fromFrame: offset * segmentFPS,
      toFrame: (offset + outroDuration) * segmentFPS
    },
    segment_metadata: {
      start: 0,
      end: duration,
      deletes: {},
      maxVideoDuration: duration,
      src: url
    }
  };
}
